/**
 * Parses a fetch-style Response body as an async iterator of Server-Sent
 * Events. We don't use the browser's `EventSource` because that API is
 * GET-only — we need POST semantics for the question + conversation_id
 * payload that `/api/v1/queries/stream` accepts.
 *
 * Yields one object per `\n\n`-terminated event with:
 *   - `event`: the event name (or `'message'` when no `event:` line was set)
 *   - `data`: the parsed JSON payload from the `data:` line (or the raw
 *     string if JSON.parse fails — defensive; the backend always JSON-encodes)
 *
 * Comments (lines starting with `:`) and any other field types are ignored.
 */
export interface SseEvent {
  event: string
  data: unknown
}

export async function* streamSseEvents(response: Response): AsyncGenerator<SseEvent, void, unknown> {
  if (!response.body) throw new Error('streaming response has no body')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      // eslint-disable-next-line no-await-in-loop -- a stream yields one chunk at a time
      const { value, done } = await reader.read()
      if (done) {
        // Flush a trailing event if the server didn't end with \n\n.
        const flushed = parseEvent(buffer.trim())
        if (flushed) yield flushed
        return
      }
      buffer += decoder.decode(value, { stream: true })
      let separator: number
      while ((separator = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, separator)
        buffer = buffer.slice(separator + 2)
        const parsed = parseEvent(raw)
        if (parsed) yield parsed
      }
    }
  } finally {
    reader.releaseLock()
  }
}

function parseEvent(raw: string): SseEvent | null {
  if (!raw || raw.startsWith(':')) return null
  let name = 'message'
  const dataLines: string[] = []
  for (const line of raw.split('\n')) {
    if (line.startsWith(':')) continue
    if (line.startsWith('event:')) name = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart())
  }
  if (dataLines.length === 0) return null
  const dataStr = dataLines.join('\n')
  let data: unknown = dataStr
  try {
    data = JSON.parse(dataStr)
  } catch {
    /* keep raw string */
  }
  return { event: name, data }
}
